import { usePrivy } from '@privy-io/react-auth';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { getPrivyPrimaryAddress } from '@/hooks';
import { primaryPrivateV2Api } from '@/lib/api-clients/rest-client';
import { NotificationChannelStatus } from '@/types';

import {
  getNotificationsQueryKey,
  NotificationChannel,
  NotificationsSettings,
} from './get-notifications-settings';

interface Body extends Pick<NotificationChannel, 'channelId'> {
  code: string;
}

async function verifyChannel(body: Body) {
  return primaryPrivateV2Api.post('/v2/notifications/email/verify', body);
}

type UseVerifyChannelOptions = {
  onError: (err: number) => void;
};

export default function useVerifyChannel(options?: UseVerifyChannelOptions) {
  const { user } = usePrivy();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Body) => verifyChannel(body),
    onSuccess: (_, variables) => {
      const address = getPrivyPrimaryAddress(user);
      queryClient.setQueryData(
        getNotificationsQueryKey(address),
        (prev?: NotificationsSettings) => {
          if (!prev) return prev;
          const index = prev.channels.findIndex(
            (channel) => channel.channelId === variables.channelId,
          );
          if (index === -1) return prev;
          const channels = [...prev.channels];
          channels[index] = {
            ...channels[index],
            status: NotificationChannelStatus.ENABLED,
          };
          return { ...prev, channels } as NotificationsSettings;
        },
      );
    },
    onError: options?.onError,
  });
}
